import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Info, X } from 'lucide-react';
import { Button } from './Button';

/**
 * Confirmation dialog — the replacement for `window.confirm`.
 *
 * The native dialog was wrong here for three reasons: it is unstyleable (so a destructive
 * action looked identical to a harmless one), it renders the raw origin ("mecaradar.com
 * says…") which reads as a browser warning rather than as the application, and it blocks the
 * JS thread so nothing can show progress while the request is in flight.
 *
 * This keeps the one genuinely good property of `window.confirm` — nothing happens until the
 * operator answers — while adding a title, a tone, a named action verb, and a busy state.
 *
 * Two ways to use it:
 *   • declaratively, rendering <ConfirmDialog open={…} …/> yourself, or
 *   • via `useConfirm()`, which returns a promise and needs no local open/close state:
 *
 *       const confirm = useConfirm();
 *       if (!(await confirm({ title: 'Deactivate?', tone: 'danger' }))) return;
 *
 * Accessibility: focus moves to the confirm button on open and is restored on close, Escape
 * cancels, Enter confirms, and focus is trapped inside the dialog while it is open.
 */
export interface ConfirmOptions {
  /** Short imperative headline — "Deactivate this relation?" */
  title: string;
  /** What actually happens, and anything irreversible about it. */
  description?: string;
  /** Label of the confirming button. Name the verb, never "OK". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` for destructive/irreversible actions, `default` for the rest. */
  tone?: 'default' | 'danger';
}

interface ConfirmDialogProps extends ConfirmOptions {
  open: boolean;
  /** Truthy while the confirmed action is in flight — disables both buttons. */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  tone = 'default',
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  // Whatever had focus before we opened, so it can be handed back on close.
  const restoreRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement as HTMLElement | null;
    confirmRef.current?.focus();
    return () => restoreRef.current?.focus?.();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) {
        e.preventDefault();
        onCancel();
        return;
      }
      if (e.key === 'Enter' && !busy) {
        e.preventDefault();
        onConfirm();
        return;
      }
      // Focus trap: keep Tab cycling inside the panel rather than escaping to the page behind.
      if (e.key !== 'Tab') return;
      const focusables = panelRef.current?.querySelectorAll<HTMLElement>('button:not([disabled])');
      if (!focusables?.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel, onConfirm]);

  if (!open) return null;

  const danger = tone === 'danger';
  const Icon = danger ? AlertTriangle : Info;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      // A backdrop click cancels, but never while the action is running.
      onClick={() => !busy && onCancel()}
    >
      <div
        ref={panelRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-describedby={description ? 'confirm-description' : undefined}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg border bg-card shadow-lg"
      >
        <div className="flex gap-3 p-5">
          <span
            className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
              danger ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'
            }`}
          >
            <Icon className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 id="confirm-title" className="text-base font-semibold text-foreground">
              {title}
            </h2>
            {description && (
              <p id="confirm-description" className="mt-1 text-sm text-muted-foreground">
                {description}
              </p>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t px-5 py-3">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={busy}>
            <X className="h-4 w-4" />
            {cancelLabel ?? t('ui.cancel')}
          </Button>
          <Button
            ref={confirmRef}
            variant={danger ? 'destructive' : 'default'}
            size="sm"
            onClick={onConfirm}
            disabled={busy}
          >
            <Check className="h-4 w-4" />
            {busy ? t('ui.working') : (confirmLabel ?? t('ui.confirm'))}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * Promise-based confirmation, so a handler reads top-to-bottom the way `window.confirm` did:
 *
 *     if (!(await confirm({ title: 'Suspend this customer?', tone: 'danger' }))) return;
 *     await apiClient.post(…);
 *
 * Returns `[confirm, dialog]` — render `dialog` once anywhere in the component.
 */
export function useConfirm(): [(opts: ConfirmOptions) => Promise<boolean>, React.ReactNode] {
  const [state, setState] = useState<ConfirmOptions | null>(null);
  // Held across renders so confirm/cancel can settle the promise the caller is awaiting.
  const resolveRef = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback((opts: ConfirmOptions) => {
    setState(opts);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const settle = useCallback((ok: boolean) => {
    setState(null);
    resolveRef.current?.(ok);
    resolveRef.current = null;
  }, []);

  const dialog = (
    <ConfirmDialog
      open={state !== null}
      title={state?.title ?? ''}
      description={state?.description}
      confirmLabel={state?.confirmLabel}
      cancelLabel={state?.cancelLabel}
      tone={state?.tone}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );

  return [confirm, dialog];
}
